"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-[11px] font-medium uppercase tracking-[0.14em] focus-visible:outline-none disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "btn-primary border shadow-[0_12px_40px_-18px_rgba(0,0,0,0.15)] hover:shadow-[0_18px_55px_-16px_rgba(0,0,0,0.25)]",
        primaryAlt:
          "btn-primary-alt bg-white text-black border border-black/10 shadow-[0_12px_40px_-18px_rgba(0,0,0,0.25)] hover:bg-white/95 hover:shadow-[0_18px_55px_-16px_rgba(0,0,0,0.4)] dark:bg-black dark:text-white dark:border-white/10 dark:shadow-[0_12px_40px_-18px_rgba(255,255,255,0.15)] dark:hover:bg-black/90 dark:hover:shadow-[0_18px_55px_-16px_rgba(255,255,255,0.25)]",
        secondary:
          "bg-muted/40 border border-border text-foreground transition-colors duration-200 hover:bg-muted hover:border-muted-foreground/20",
        outline:
          "border border-border text-foreground bg-transparent transition-colors duration-200 hover:bg-foreground/[0.08]",
        ghost: "text-foreground hover:bg-foreground/[0.08] transition-colors duration-200",
        link: "text-foreground underline-offset-4 hover:underline transition-colors duration-200",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-glow-red",
      },
      size: {
        xs: "h-8 px-3.5 text-[9px] tracking-[0.10em]",
        s: "h-9 px-5 text-[10px] tracking-[0.12em]",
        m: "h-10 px-6 text-[10px] tracking-[0.12em]",
        l: "h-12 px-8 text-[10px] tracking-[0.12em]",
        icon: "h-10 w-10 p-0 tracking-[0.12em]",
        iconSm: "h-8 w-8 p-0 tracking-[0.12em]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "m",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
